import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const ReviewStep = ({ formData, errors, onChange, onEdit }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getDocumentCount = () => {
    return Object.keys(formData?.documents || {})?.length;
  };

  const sections = [
    {
      id: 'personal',
      title: 'Personal Information',
      icon: 'User',
      step: 1,
      fields: [
        { label: 'Full Name', value: `${formData?.firstName || ''} ${formData?.lastName || ''}` },
        { label: 'Date of Birth', value: formatDate(formData?.dateOfBirth) },
        { label: 'Gender', value: formData?.gender || 'Not specified' },
        { label: 'Nationality', value: formData?.nationality || 'Not specified' },
        { label: 'Passport Number', value: formData?.passportNumber || 'Not provided' },
        { label: 'Email', value: formData?.email || 'Not provided' },
        { label: 'Phone', value: formData?.phone || 'Not provided' },
        { label: 'Address', value: `${formData?.streetAddress || ''}, ${formData?.city || ''}, ${formData?.state || ''}, ${formData?.postalCode || ''}, ${formData?.country || ''}` }
      ]
    },
    {
      id: 'education',
      title: 'Educational Background',
      icon: 'BookOpen',
      step: 2,
      fields: [
        { label: 'Highest Education', value: formData?.highestEducation || 'Not specified' },
        { label: 'Institution', value: formData?.institutionName || 'Not provided' },
        { label: 'Field of Study', value: formData?.fieldOfStudy || 'Not specified' },
        { label: 'GPA/Grade', value: formData?.gpa || 'Not provided' },
        { label: 'Graduation Date', value: formatDate(formData?.graduationDate) },
        { label: 'Chinese Level', value: formData?.chineseLevel || 'Not specified' },
        { label: 'HSK Score', value: formData?.hskScore || 'Not taken' },
        { label: 'Years of Chinese Study', value: formData?.yearsOfChinese ? `${formData?.yearsOfChinese} years` : 'Not specified' }
      ]
    },
    {
      id: 'program',
      title: 'Program Selection',
      icon: 'GraduationCap',
      step: 3,
      fields: [
        { label: 'Institution', value: formData?.institution || 'Not selected' },
        { label: 'Program Type', value: formData?.programType || 'Not selected' },
        { label: 'Duration', value: formData?.duration || 'Not selected' },
        { label: 'Intake Period', value: formData?.intake || 'Not selected' },
        { label: 'Accommodation', value: formData?.accommodation || 'Not selected' },
        { label: 'Airport Pickup', value: formData?.airportPickup ? 'Yes' : 'No' },
        { label: 'Preferred Currency', value: formData?.preferredCurrency?.toUpperCase() || 'USD' }
      ]
    },
    {
      id: 'documents',
      title: 'Documents',
      icon: 'FileText',
      step: 4,
      fields: [
        { label: 'Documents Uploaded', value: `${getDocumentCount()} files` }
      ]
    }
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl md:text-3xl font-heading font-semibold text-foreground mb-2">
          Review & Submit
        </h2>
        <p className="text-sm md:text-base text-muted-foreground font-caption">
          Please review all information carefully before submitting your application.
        </p>
      </div>
      <div className="space-y-4">
        {sections?.map((section) => (
          <div key={section?.id} className="bg-card rounded-lg border border-border overflow-hidden">
            <div className="flex items-center justify-between p-4 md:p-6 bg-muted/30 border-b border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name={section?.icon} size={20} color="var(--color-primary)" />
                </div>
                <h3 className="text-base md:text-lg font-heading font-semibold text-foreground">
                  {section?.title}
                </h3>
              </div>
              <Button
                variant="ghost"
                size="sm"
                iconName="Edit"
                iconPosition="left"
                onClick={() => onEdit(section?.step)}
              >
                Edit
              </Button>
            </div>

            <div className="p-4 md:p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {section?.fields?.map((field, index) => (
                  <div key={index} className="space-y-1">
                    <p className="text-xs md:text-sm font-caption font-medium text-muted-foreground">
                      {field?.label}
                    </p>
                    <p className="text-sm md:text-base text-foreground">
                      {field?.value || 'Not provided'}
                    </p>
                  </div>
                ))}
              </div>

              {section?.id === 'documents' && formData?.documents && (
                <div className="mt-4 pt-4 border-t border-border">
                  <div className="space-y-2">
                    {Object.entries(formData?.documents)?.map(([docId, doc]) => (
                      <div key={docId} className="flex items-center gap-2 text-sm">
                        <Icon name="CheckCircle" size={16} color="var(--color-success)" />
                        <span className="text-foreground font-caption">{doc?.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="p-4 md:p-6 bg-accent/10 rounded-lg border border-accent/20">
        <div className="flex items-start gap-3">
          <Icon name="AlertCircle" size={20} color="var(--color-accent)" />
          <div>
            <p className="text-sm md:text-base font-medium text-foreground mb-2">
              Important Notice
            </p>
            <ul className="text-xs md:text-sm text-muted-foreground font-caption space-y-1">
              <li>• Ensure all information is accurate and matches your official documents</li>
              <li>• Application processing typically takes 2-4 weeks</li>
              <li>• You will receive email updates on your application status</li>
              <li>• Application fee is non-refundable once submitted</li>
            </ul>
          </div>
        </div>
      </div>
      <div className="space-y-4 p-4 md:p-6 bg-card rounded-lg border border-border">
        <h3 className="text-base md:text-lg font-heading font-semibold text-foreground">
          Declaration & Consent
        </h3>

        <div className="space-y-3">
          <Checkbox
            label="I declare that all information provided is true and accurate"
            description="False information may result in application rejection"
            checked={formData?.declarationAccurate || false}
            onChange={(e) => onChange('declarationAccurate', e?.target?.checked)}
            error={errors?.declarationAccurate}
            required
          />

          <Checkbox
            label="I agree to the terms and conditions"
            description="Please read the terms before accepting"
            checked={formData?.termsAccepted || false}
            onChange={(e) => onChange('termsAccepted', e?.target?.checked)}
            error={errors?.termsAccepted}
            required
          />

          <Checkbox
            label="I consent to the processing of my personal data"
            description="Your data will be handled according to privacy policy"
            checked={formData?.dataConsent || false}
            onChange={(e) => onChange('dataConsent', e?.target?.checked)}
            error={errors?.dataConsent}
            required
          />

          <Checkbox
            label="I agree to receive email communications about my application"
            checked={formData?.emailConsent || false}
            onChange={(e) => onChange('emailConsent', e?.target?.checked)}
          />
        </div>
      </div>
      <div className="p-4 md:p-6 bg-primary/5 rounded-lg border border-primary/20">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm md:text-base font-caption font-medium text-foreground mb-1">
              Application Fee
            </p>
            <p className="text-xs md:text-sm text-muted-foreground font-caption">
              Non-refundable processing fee
            </p>
          </div>
          <div className="text-right">
            <p className="text-xl md:text-2xl font-heading font-semibold text-primary data-text">
              {formData?.preferredCurrency === 'eur' && '€85.00'}
              {formData?.preferredCurrency === 'cny' && '¥650.00'}
              {(!formData?.preferredCurrency || formData?.preferredCurrency === 'usd') && '$100.00'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewStep;